// web/src/events/api.ts
// Centralized client helpers for Events: sharing, followers/following, search, RSVPs.

import { auth, db, functions } from "@/lib/firebase";
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAt,
  endAt,
  where,
  DocumentReference,
  Unsubscribe,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

/* =========================
   Types
========================= */

export type Person = {
  uid: string;
  username: string;
  usernameLower?: string;
  avatarUrl: string | null;
};

export type SharePayload = {
  eid: string;
  toUid: string;
  fromUid: string;
  note?: string | null;
};

export type RsvpUser = {
  uid: string;
  username?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  createdAt?: any;
};

/* =========================
   Utils
========================= */

function requireAuthed(): string {
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in");
  return u.uid;
}

function sanitizeLower(s: string | null | undefined): string {
  return (s || "").trim().toLowerCase();
}

/* =========================
   Share to users (callable)
========================= */

/** Share a single event to one user (server creates inbox message). */
export async function shareEvent(eid: string, toUid: string, note?: string) {
  const fromUid = requireAuthed();
  const fn = httpsCallable(functions, "shareEventToUser");
  await fn({ eid, toUid, fromUid, note: note ?? null } as SharePayload);
}

/** Share to many users (serialized to keep within callable QPS safely). */
export async function shareEventToMany(
  eid: string,
  toUids: string[],
  note?: string
) {
  for (const toUid of Array.from(new Set(toUids))) {
    // no await Promise.all: be gentle to backends + preserves order
    await shareEvent(eid, toUid, note);
  }
}

/* =========================
   Followers / Following
   Expected schemas (denorm recommended):
   - /users/{uid}/followers/{followerUid} { username, usernameLower, avatarUrl }
   - /users/{uid}/following/{targetUid}  { username, usernameLower, avatarUrl }
   If denorm fields are absent, we fallback to reading /users/{uid}.
========================= */

async function resolveUserDoc(uid: string): Promise<Person> {
  const u = await getDoc(doc(db, "users", uid));
  const x = u.exists() ? (u.data() as any) : {};
  const display = x.username || x.usernameLower || "player";
  return {
    uid: u.id,
    username: display,
    usernameLower: (x.usernameLower as string) ?? sanitizeLower(display),
    avatarUrl: (x.avatarUrl as string) ?? null,
  };
}

async function mapSubDocAsPerson(
  snapshotDoc: firebase.default.firestore.QueryDocumentSnapshot<firebase.default.firestore.DocumentData, firebase.default.firestore.DocumentData>
): Promise<Person> {
  const x = snapshotDoc.data() as any;
  // If denorm present, use it; else fallback to full user doc
  if (x && (x.username || x.usernameLower || x.avatarUrl)) {
    return {
      uid: snapshotDoc.id,
      username: (x.username as string) || (x.usernameLower as string) || "player",
      usernameLower:
        (x.usernameLower as string) ||
        sanitizeLower((x.username as string) || ""),
      avatarUrl: (x.avatarUrl as string) ?? null,
    };
  }
  // fallback
  return resolveUserDoc(snapshotDoc.id);
}

/** Fetch followers of current user (sorted by username). */
export async function getMyFollowers(): Promise<Person[]> {
  const me = requireAuthed();
  const snap = await getDocs(collection(db, "users", me, "followers"));
  const list = await Promise.all(snap.docs.map(mapSubDocAsPerson));
  list.sort((a, b) => a.username.localeCompare(b.username));
  return list;
}

/** Fetch people I follow (optional tab). */
export async function getMyFollowing(): Promise<Person[]> {
  const me = requireAuthed();
  const snap = await getDocs(collection(db, "users", me, "following"));
  const list = await Promise.all(snap.docs.map(mapSubDocAsPerson));
  list.sort((a, b) => a.username.localeCompare(b.username));
  return list;
}

/* =========================
   Username prefix search
   Requires single-field index on users.usernameLower (default on).
========================= */

export async function searchUsersByUsername(
  prefix: string,
  max = 25,
  opts?: { excludeUids?: string[]; includeFollowingFirst?: boolean }
): Promise<Person[]> {
  const me = auth.currentUser?.uid || null;
  const p = sanitizeLower(prefix);
  if (!p) return [];

  const qy = query(
    collection(db, "users"),
    orderBy("usernameLower"),
    startAt(p),
    endAt(p + "\uf8ff"),
    limit(max)
  );
  const snap = await getDocs(qy);
  let hits: Person[] = snap.docs.map((d) => {
    const x = d.data() as any;
    const display = x.username || x.usernameLower || "player";
    return {
      uid: d.id,
      username: display,
      usernameLower: (x.usernameLower as string) ?? sanitizeLower(display),
      avatarUrl: (x.avatarUrl as string) ?? null,
    };
  });

  // Exclude self + any provided excludes
  const excludeSet = new Set<string>([
    ...(me ? [me] : []),
    ...((opts?.excludeUids ?? []) as string[]),
  ]);
  hits = hits.filter((h) => !excludeSet.has(h.uid));

  // Optional: bubble up people I already follow
  if (opts?.includeFollowingFirst && me) {
    // Cheap client-side heuristic: anything with 'isFollowed' denorm later; for now, stable sort by username
    hits.sort((a, b) => a.username.localeCompare(b.username));
  }

  return hits;
}

/* =========================
   RSVP helpers
========================= */

export async function setRsvp(eid: string, going: boolean) {
  const user = auth.currentUser;
  if (!user) throw new Error("Sign in required.");
  const uid = user.uid;

  // Preflight: must be able to read the event
  try {
    const evSnap = await getDoc(doc(db, "events", eid));
    if (!evSnap.exists()) throw new Error("event_not_found_or_inaccessible");
  } catch {
    throw new Error("event_not_found_or_inaccessible");
  }

  const ref = doc(db, "events", eid, "rsvps", uid);

  if (!going) {
    await deleteDoc(ref);
    return;
  }

  // Denormalize basic user fields for chips
  let username: string | null = null;
  let displayName: string | null = null;
  let avatarUrl: string | null = null;
  try {
    const prof = await getDoc(doc(db, "users", uid));
    if (prof.exists()) {
      const d = prof.data() as any;
      username = d.username ?? null;
      displayName = d.displayName ?? d.storeName ?? d.name ?? username ?? null;
      avatarUrl = d.avatarUrl ?? d.photoURL ?? d.storeImage ?? d?.lgs?.avatar_url ?? null;
    }
  } catch {}

  await setDoc(
    ref,
    {
      uid,
      going: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      user: { username, displayName, avatarUrl },
    },
    { merge: true }
  );
}

// Subscribe to the event's rsvpCount (with fallback to subcollection size)
export function subscribeRsvpCount(
  eid: string,
  cb: (count: number) => void
): () => void {
  const eventRef = doc(db, "events", eid);
  const unsubEvent = onSnapshot(
    eventRef,
    (snap) => {
      if (snap.exists() && typeof (snap.data() as any).rsvpCount === "number") {
        cb((snap.data() as any).rsvpCount);
      }
    },
    () => {}
  );

  // Fallback: count RSVP docs if rsvpCount isn't present
  const qy = query(collection(db, "events", eid, "rsvps"), orderBy("createdAt", "asc"));
  const unsubRsvps = onSnapshot(
    qy,
    (snap) => cb(snap.size),
    () => {}
  );

  return () => {
    unsubEvent();
    unsubRsvps();
  };
}

// Subscribe to RSVP user list (for chips)
export function subscribeRsvps(
  eid: string,
  cb: (users: RsvpUser[]) => void
): () => void {
  const qy = query(collection(db, "events", eid, "rsvps"), orderBy("createdAt", "asc"));
  return onSnapshot(
    qy,
    (snap) => {
      const arr: RsvpUser[] = snap.docs.map((d) => {
        const v = d.data() as any;
        const u = v.user || {};
        return {
          uid: v.uid || d.id,
          username: u.username ?? null,
          displayName: u.displayName ?? null,
          avatarUrl: u.avatarUrl ?? null,
          createdAt: v.createdAt,
        };
      });
      cb(arr);
    },
    () => cb([])
  );
}

/* =========================
   Internal Shares (Inbox quick helpers)
   Useful if you prefer client-side write (bypassing callable) for prototypes,
   but prefer the callable shareEvent() in production to centralize logic.
========================= */

export async function writeInboxEvent(toUid: string, eid: string, note?: string) {
  const fromUid = requireAuthed();
  // Server rules allow only the recipient to write to their inbox; thus this helper
  // is only valid if rules allow system clients; prefer the callable 'shareEvent'.
  await setDoc(doc(collection(db, "users", toUid, "inbox_events")), {
    eid,
    fromUid,
    createdAt: new Date(),
    read: false,
    note: note ?? null,
  });
}

